/**
 * Admin assignment management hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import { invalidateMissingRuntimeConfig } from './useMissingRuntimeConfig';
import type { Assignment, AssignmentWithDepsResponse, AuditLogEntry } from '../schemas/admin.schemas';

const assignmentsKey = (projectId: string) => [
  'admin-assignments',
  projectId,
];
const auditKey = (projectId: string) => ['admin-audit', projectId];

export function useProjectAssignments(projectId: string) {
  return useQuery<Assignment[]>({
    queryKey: assignmentsKey(projectId),
    queryFn: () => adminApi.listProjectFeatures(projectId),
    enabled: projectId.length > 0,
  });
}

export function useAssignFeature() {
  const queryClient = useQueryClient();

  return useMutation<
    AssignmentWithDepsResponse,
    Error,
    { projectId: string; featureId: string; projectName: string; options?: Record<string, unknown> }
  >({
    mutationFn: ({
      projectId,
      featureId,
      projectName,
      options,
    }) => adminApi.assignFeature(projectId, featureId, projectName, options),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: assignmentsKey(variables.projectId),
      });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
      options,
    }: {
      assignmentId: string;
      options: Record<string, unknown> | null;
      projectId: string;
    }) => adminApi.updateAssignment(assignmentId, options),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: assignmentsKey(variables.projectId),
      });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useRemoveAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
    }: {
      assignmentId: string;
      projectId: string;
    }) => adminApi.removeAssignment(assignmentId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: assignmentsKey(variables.projectId),
      });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useAuditLog(projectId: string) {
  return useQuery<AuditLogEntry[]>({
    queryKey: auditKey(projectId),
    queryFn: () => adminApi.getAuditLog(projectId),
    enabled: projectId.length > 0,
  });
}
